import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { SkeletonCard } from '@/components/ui/Skeleton'
import { Pagination } from '@/components/ui/Pagination'
import { ProjectListCard } from '@/components/projects/projectlistcard'
import { usePaginatedList } from '@/hooks/usePaginatedList'
import { projectApi, type ProjectListParams } from '@/api/projectApi'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useTabsStore, MAX_OPEN_TABS } from '@/stores/tabsStore'
import { useTabLimitStore } from '@/stores/tabLimitStore'
import type { ProjectListItem } from '@/api/types'

const PAGE_SIZE = 4

interface OpenProjectModalProps {
  open: boolean
  onClose: () => void
}

export function OpenProjectModal({ open, onClose }: OpenProjectModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace)
  const tabs = useTabsStore((s) => s.tabs)
  const showTabLimit = useTabLimitStore((s) => s.show)

  const workspaceId = currentWorkspace?.id

  const { items, total, loading, page, totalPages, filters, setFilter, setPage, reload } =
    usePaginatedList<ProjectListItem, ProjectListParams>(
      (params) => projectApi.list(workspaceId!, params),
      {
        initialLimit: PAGE_SIZE,
        initialFilters: {},
        enabled: open && !!workspaceId,
      },
    )

  useEffect(() => {
    if (open) reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const openProjectIds = useMemo(
    () => new Set(tabs.map((t) => t.projectId).filter(Boolean)),
    [tabs],
  )

  function handleSelect(project: ProjectListItem) {
    if (!currentWorkspace) return
    const url = `/workspace/${currentWorkspace.slug}/projects/${project.id}/editor`
    // Already open — just navigate, no new tab will be created
    const alreadyOpen = tabs.some((tab) => tab.projectId === String(project.id))
    if (!alreadyOpen && tabs.length >= MAX_OPEN_TABS) {
      onClose()
      showTabLimit(url)
      return
    }
    onClose()
    navigate(url)
  }

  const query = (filters.name as string) ?? ''

  return (
    <Dialog
      open={open}
      title={t('tabs.openProjectTitle', 'Abrir projeto')}
      onClose={onClose}
      size="lg"
      footer={
        <Button variant="ghost" onClick={onClose}>
          {t('common.cancel')}
        </Button>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0 }}>
        {/* Search — fixed, never scrolls */}
        <div style={{ flexShrink: 0 }}>
          <input
            type="text"
            value={query}
            onChange={(e) => setFilter('name', e.target.value)}
            placeholder={t('tabs.openProjectSearch', 'Buscar por nome...')}
            className="morph-input"
            autoFocus
          />
        </div>

        {/* Cards — scroll area */}
        <div style={{ overflowY: 'auto' }}>
          {loading ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 10,
              }}
            >
              {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div
              style={{
                padding: '24px 0',
                textAlign: 'center',
                fontSize: 12,
                color: 'var(--text-dim)',
                border: '1px solid var(--border)',
                borderRadius: 8,
              }}
            >
              {total === 0 && !query
                ? t('tabs.openProjectEmpty', 'Nenhum projeto neste workspace.')
                : t('tabs.openProjectNoMatch', 'Nenhum projeto corresponde à busca.')}
            </div>
          ) : (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                  gap: 10,
                }}
              >
                {items.map((project) => (
                  <ProjectListCard
                    key={project.id}
                    project={project}
                    onOpen={handleSelect}
                    isOpen={openProjectIds.has(String(project.id))}
                  />
                ))}
              </div>
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </>
          )}
        </div>
      </div>
    </Dialog>
  )
}
